import {DriveLogStatus} from "@/shared/timr";
import {EMPTY_CALLBACK} from "@/shared/utils/misc";

// Global declarations for non-module types
declare const c3: typeof import("c3");
declare const d3: typeof import("d3");

export interface DriveLogSums {
	durationNet: number;
	distance: number;
}

export interface DriveLogUserSums {
	businessSums: DriveLogSums;
	privateSums: DriveLogSums;
}

export class DriveLogStatistics {

	private container: JQuery;
	private totalChart: c3.ChartAPI;

	constructor(container: JQuery) {
		this.container = container;
		this.init();
	}

	/**
	 * Updates the drive log statistics based on the provided drive log status.
	 *
	 * @param driveLogStatus The current status of the drive log, including the sums for the current user
	 */
	public updateStatistics(driveLogStatus: DriveLogStatus) {
		const {driveLogUserSums} = driveLogStatus;

		let distanceBusiness = 0;
		let distancePrivate = 0;
		if (driveLogUserSums.businessSums) {
			distanceBusiness = driveLogUserSums.businessSums.distance;
		}
		if (driveLogUserSums.privateSums) {
			distancePrivate = driveLogUserSums.privateSums.distance;
		}
		const totalDistance = "" + (distanceBusiness + distancePrivate);
		this.totalChart.load({
			columns: this.buildChartColumns(distanceBusiness, distancePrivate)
		});
		this.container.find("#driveLogDistanceBusiness").text(distanceBusiness);
		this.container.find("#driveLogDistanceTotal").text(totalDistance);
		if ($(".statistics-chart-popover").length === 0) {
			this.container.find("#driveLogTotalChartPopover").popover("destroy").popover({
				animation: false,
				trigger: "hover",
				placement: "top",
				container: ".content-wrapper",
				delay: {show: 400, hide: 0},
				html: true,
				template: `<div class="popover statistics-chart-popover" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>`,
				content: () => this.renderChartPopover(distanceBusiness, distancePrivate)
			});
		}
	}

	private init() {
		this.totalChart = this.initChart("#driveLogTotalChart");
	}

	private initChart(bindTo: string): c3.ChartAPI {
		return c3.generate({
			bindto: bindTo,
			size: {height: 110},
			interaction: {enabled: false},
			data: {
				// series colours come from CSS (components/charts.less, .c3-arc-<id>), themed there
				names: {
					distanceBusiness: messages["statisticsPanel.drivelog.business"],
					distancePrivate: messages["statisticsPanel.drivelog.private"]
				},
				columns: this.buildChartColumns(0, 0),
				type: "donut",
				order: null
			},
			legend: {
				show: false,
				item: {
					onclick: EMPTY_CALLBACK
				}
			},
			donut: {
				label: {
					show: false
				},
				width: 7
			}
		});
	}

	private buildChartColumns(distanceBusiness: number, distancePrivate: number): c3.PrimitiveArray[] {
		return [
			["empty", distanceBusiness === 0 && distancePrivate === 0 ? 1 : 0],
			["distanceBusiness", distanceBusiness],
			["distancePrivate", distancePrivate]
		];
	}

	protected renderChartPopover(distanceBusiness: number, distancePrivate: number) {
		if (distanceBusiness === 0 && distancePrivate === 0) {
			return `<table class="statistics-chart-popover-table-empty">
							<tr>
								<td class="text-center"><i class="fa fa-circle statistics-chart-popover-circle-empty"></i> ${messages["statisticsPanel.record.empty"]}</td>
							</tr>
						</table>`;
		} else {
			const sum = distanceBusiness + distancePrivate;
			return `<table class="statistics-chart-popover-table">
							<tr>
								<td><i class="fa fa-circle statistics-chart-popover-circle-business"></i> ${messages["statisticsPanel.drivelog.business"]}</td>
								<td class="duration-width text-right">${(distanceBusiness)}</td>
								<td class="duration-width text-right">(${d3.format(".2%")(distanceBusiness / sum)})</td>
							</tr>
							<tr class="statistics-chart-popover-total-line">
								<td><i class="fa fa-circle statistics-chart-popover-circle-private"></i> ${messages["statisticsPanel.drivelog.private"]}</td>
								<td class="duration-width text-right">${(distancePrivate)} </td>
								<td class="duration-width text-right">(${d3.format(".2%")(distancePrivate / sum)})</td>
							</tr>
							<tr class="statistics-chart-sum">
								<td><i class="fa fa-circle statistics-chart-popover-circle-transparent"></i> ${messages["statisticsPanel.record.sum"]}</td>
								<td class="duration-width text-right">${sum}</td>
							</tr>
						</table>`;
		}
	}
}
